import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar } from './Sidebar';
import { Toaster } from '../ui/sonner';
import { BetaBanner } from '../beta/BetaBanner';
import { FeedbackDialog } from '../beta/FeedbackDialog';

export const MainLayout = () => {
    const { isAuthenticated, loading, isEndUser } = useAuth();
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [bugReportOpen, setBugReportOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const location = useLocation();

    // Close the mobile drawer whenever the route changes (e.g. after tapping a nav link).
    useEffect(() => { setNavOpen(false); }, [location.pathname]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // End users (business owners) never see the accountant app — send them to their inbox.
    if (isEndUser) {
        return <Navigate to="/inbox" replace />;
    }

    return (
        <div className="min-h-screen bg-background">
            <BetaBanner
                onOpenFeedback={() => setFeedbackOpen(true)}
                onOpenBugReport={() => setBugReportOpen(true)}
            />
            {/* Sidebar: off-canvas drawer on mobile, fixed on lg+ */}
            <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

            {/* Content column — no left margin on mobile, 64-wide margin on lg+ */}
            <div className="lg:ml-64">
                {/* Mobile top bar with hamburger (lg+ uses the sidebar instead) */}
                <div className="lg:hidden sticky top-0 z-30 h-14 flex items-center gap-3 border-b border-border bg-card px-4">
                    <button
                        onClick={() => setNavOpen(true)}
                        className="p-1.5 -ml-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                        aria-label="Open menu"
                        data-testid="mobile-menu-btn"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <span className="font-heading font-bold text-primary">Accountrix</span>
                </div>

                <main className="min-h-screen">
                    <div className="p-4 sm:p-6 lg:p-8">
                        <Outlet />
                    </div>
                </main>
            </div>

            <Toaster position="top-right" richColors />

            {/* Feedback Dialogs */}
            <FeedbackDialog
                open={feedbackOpen}
                onOpenChange={setFeedbackOpen}
                type="feedback"
            />
            <FeedbackDialog
                open={bugReportOpen}
                onOpenChange={setBugReportOpen}
                type="bug"
            />
        </div>
    );
};
